import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  getProcurementSettings, 
  updateProcurementSettings 
} from '../../../models/procurementModel';
import { useHasAnyPermission } from '../../../utils/permissions';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import CustomDatePicker from '../../../components/common/CustomDatePicker';
import { format, parse } from 'date-fns';

const ProcurementSettings = () => {
  const canViewSettings = useHasAnyPermission(['Access Procurement Settings', 'View Settings']);
  const canEditSettings = useHasAnyPermission(['Modify Procurement Settings', 'Edit Settings']);
 
  const [settings, setSettings] = useState({
    // Upload Settings (from PHP)
    site_allowed_ext: 'pdf,doc,docx,xls,xlsx,txt,jpg,jpeg,png,gif',
    site_upload_limit: '10',
    site_enforce_limit: '0',
    
    // Approval Settings (from PHP)
    site_limit_on: '0',
    site_limit: '5000',
    site_limit_above: '0',
    
    // Bidding Settings
    site_bid_opening: '',
    site_bid_closing: '',
    site_show_bidding: '0',
    site_bid_disclaimer: '',

    // RFQ Settings (from PHP)
    site_rfq_notice: '',
    site_quote_terms: '',
    site_epr_footer: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (canViewSettings) {
      loadSettings();
    } else {
      setLoading(false);
      setError('You do not have permission to view settings.');
    }
  }, [canViewSettings]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getProcurementSettings();
      if (data) {
        // Merge with defaults
        const mergedSettings = { ...settings };
        Object.keys(data).forEach(key => {
          mergedSettings[key] = data[key] || settings[key];
        });
        setSettings(mergedSettings);
      }
    } catch (error) {
      console.error('Error loading procurement settings:', error);
      setError('Failed to load procurement settings');
      toast.error('Failed to load procurement settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDateChange = (key, dateStr) => {
    if (!dateStr) {
        handleInputChange(key, '');
        return;
    }
    
    const parsedDate = parse(dateStr, 'dd/MM/yyyy', new Date());
    const datePart = format(parsedDate, 'yyyy-MM-dd');
    
    let timePart = '12:00';
    if (settings[key]) {
        const current = new Date(settings[key]);
        if (!isNaN(current)) {
            timePart = format(current, 'HH:mm');
        }
    }
    
    handleInputChange(key, `${datePart}T${timePart}`);
  };

  const handleTimeChange = (key, timeValue) => {
    const currentDateTime = settings[key];
    let datePart = format(new Date(), 'yyyy-MM-dd');
    
    if (currentDateTime) {
         const dateObj = new Date(currentDateTime);
         if (!isNaN(dateObj)) {
             datePart = format(dateObj, 'yyyy-MM-dd');
         }
    }
    
    handleInputChange(key, `${datePart}T${timeValue}`);
  };

  const handleSave = async () => {
    if (!canEditSettings) {
      toast.error('You do not have permission to edit settings.');
      return;
    }

    try {
      setSaving(true);
      await updateProcurementSettings(settings);
      toast.success('Procurement settings updated successfully');
    } catch (error) {
      console.error('Error saving procurement settings:', error);
      toast.error('Failed to save procurement settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Professional Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0096DE" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Procurement Settings</h1>
                <p className="text-xs text-gray-500 mb-1">
                  Configure procurement system settings and policies
                </p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0096DE' }}>
                    System Configuration
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: Action Buttons */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              <div className="flex items-center gap-2">
                {/* Reset Button */}
                <button
                  onClick={loadSettings}
                  disabled={saving}
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                  title="Reset Settings"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reset
                </button>
                
                {/* Save Button */}
                <button
                  onClick={handleSave}
                  disabled={saving || !canEditSettings}
                  className="inline-flex items-center px-4 py-1.5 text-white text-sm font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200 disabled:opacity-50"
                  style={{ 
                    backgroundColor: '#0096DE', 
                    boxShadow: '0 1px 2px rgba(0, 150, 222, 0.3)' 
                  }}
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Save Settings
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
          
          {/* Left Column: Compact Settings (4 cols) */}
          <div className="xl:col-span-4 space-y-4">
            
            {/* File Upload Settings */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>
                File Uploads
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Allowed Extensions</label>
                  <input
                    type="text"
                    value={settings.site_allowed_ext}
                    onChange={(e) => handleInputChange('site_allowed_ext', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="pdf,doc,docx..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Size Limit (MB)</label>
                    <input
                      type="number"
                      value={settings.site_upload_limit}
                      onChange={(e) => handleInputChange('site_upload_limit', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Enforce Limit</label>
                    <select
                      value={settings.site_enforce_limit}
                      onChange={(e) => handleInputChange('site_enforce_limit', e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="1">Yes</option>
                      <option value="0">No</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Approval Settings */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                Approvals
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Approval Limit (R)</label>
                  <input
                    type="number"
                    value={settings.site_limit}
                    onChange={(e) => handleInputChange('site_limit', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="bg-gray-50 rounded p-3 space-y-2 border border-gray-100">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="site_limit_on"
                      checked={settings.site_limit_on === '1'}
                      onChange={(e) => handleInputChange('site_limit_on', e.target.checked ? '1' : '0')}
                      className="mt-0.5 h-3.5 w-3.5 text-blue-600 border-gray-300 rounded"
                    />
                    <label htmlFor="site_limit_on" className="ml-2 text-xs text-gray-600 leading-tight">
                      Requisitions &lt; limit need Manager (3rd) approval
                    </label>
                  </div>
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="site_limit_above"
                      checked={settings.site_limit_above === '1'}
                      onChange={(e) => handleInputChange('site_limit_above', e.target.checked ? '1' : '0')}
                      className="mt-0.5 h-3.5 w-3.5 text-blue-600 border-gray-300 rounded"
                    />
                    <label htmlFor="site_limit_above" className="ml-2 text-xs text-gray-600 leading-tight">
                      Require Manager approval first if &gt; limit
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Bidding Settings */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                Bidding
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded border border-blue-100">
                  <label htmlFor="site_show_bidding" className="text-xs font-medium text-blue-800 cursor-pointer select-none">Enable Bidding Module</label>
                  <div className="relative inline-block w-8 align-middle select-none">
                    <input
                      type="checkbox"
                      id="site_show_bidding"
                      checked={settings.site_show_bidding == 1}
                      onChange={(e) => handleInputChange('site_show_bidding', e.target.checked ? '1' : '0')}
                      className="toggle-checkbox absolute block w-4 h-4 rounded-full bg-white border-4 appearance-none cursor-pointer"
                      style={{ 
                        right: settings.site_show_bidding == 1 ? '0' : 'auto', 
                        left: settings.site_show_bidding == 1 ? 'auto' : '0',
                        transition: 'all 0.3s ease'
                      }}
                    />
                    <label 
                      htmlFor="site_show_bidding" 
                      className={`toggle-label block overflow-hidden h-4 rounded-full cursor-pointer transition-colors duration-300 ${settings.site_show_bidding == 1 ? 'bg-blue-500' : 'bg-gray-300'}`}
                    ></label>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Global Opening</label>
                    <div className="flex gap-2">
                      <div className="w-3/5">
                          <CustomDatePicker 
                              value={settings.site_bid_opening}
                              onChange={(val) => handleDateChange('site_bid_opening', val)}
                              className="text-xs w-full"
                          />
                      </div>
                      <div className="w-2/5">
                        <input
                            type="time"
                            value={settings.site_bid_opening ? format(new Date(settings.site_bid_opening), 'HH:mm') : ''}
                            onChange={(e) => handleTimeChange('site_bid_opening', e.target.value)}
                            className="px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-full h-[34px]"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Global Closing</label>
                    <div className="flex gap-2">
                      <div className="w-3/5">
                          <CustomDatePicker 
                              value={settings.site_bid_closing}
                              onChange={(val) => handleDateChange('site_bid_closing', val)}
                              className="text-xs w-full"
                          />
                      </div>
                      <div className="w-2/5">
                        <input
                            type="time"
                            value={settings.site_bid_closing ? format(new Date(settings.site_bid_closing), 'HH:mm') : ''}
                            onChange={(e) => handleTimeChange('site_bid_closing', e.target.value)}
                            className="px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-full h-[34px]"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Bid Disclaimer</label>
                  <textarea
                    value={settings.site_bid_disclaimer}
                    onChange={(e) => handleInputChange('site_bid_disclaimer', e.target.value)}
                    rows={4}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter the disclaimer text that users must accept before placing their first bid on an item..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Users must accept this disclaimer when placing their first bid on each item.
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* Right Column: RFQ Settings (8 cols) */}
          <div className="xl:col-span-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-full">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center">
                <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                RFQ Configuration
              </h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    RFQ Notice
                  </label>
                  <ReactQuill
                    value={settings.site_rfq_notice}
                    onChange={(value) => handleInputChange('site_rfq_notice', value)}
                    theme="snow"
                    className="bg-white"
                    style={{ height: '150px', marginBottom: '50px' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Standard Terms and Conditions
                  </label>
                  <ReactQuill
                    value={settings.site_quote_terms}
                    onChange={(value) => handleInputChange('site_quote_terms', value)}
                    theme="snow"
                    className="bg-white"
                    style={{ height: '200px', marginBottom: '50px' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Requisition Footer
                  </label>
                  <ReactQuill
                    value={settings.site_epr_footer}
                    onChange={(value) => handleInputChange('site_epr_footer', value)}
                    theme="snow"
                    className="bg-white"
                    style={{ height: '120px', marginBottom: '50px' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcurementSettings;
